// frontend/src/tabs/routeRegistry.jsx
//
// Single source of truth for the protected page routes. Both
// App.jsx (to feed createBrowserRouter) and TabsHost (to render each
// open tab by its path) read this. Without this consolidation the two
// would drift — exactly the duplication CLAUDE.md's
// "map consumers before changing semantics" rule exists to prevent.
//
// Public routes (login / setup-account / forgot-password / reset-password /
// confirm-email) stay in App.jsx — they're not part of the tabbed shell. Layouts
// (RootLayout, ProtectedLayout, StaffGuard) also stay in App.jsx
// because they own router-bound behavior (auth checks, billing gate).

import { matchPath } from 'react-router-dom';

// Page imports — THE place that imports pages into the route shell. App.jsx and TabsHost both consume from here.
import Home from '../pages/Home';
import Inventory from '../pages/Inventory';
import Forecast from '../pages/Forecast';
import Promotions from '../pages/Promotions';
import ReportsHub from '../pages/reports/ReportsHub';
import SalesForecastAccuracyReport from '../pages/reports/SalesForecastAccuracyReport';
import StockoutReport from '../pages/reports/StockoutReport';
import ExcessStockReport from '../pages/reports/ExcessStockReport';
import ClassificationReport from '../pages/reports/ClassificationReport';
import GMROIReport from '../pages/reports/GMROIReport';
import DeadStockReport from '../pages/reports/DeadStockReport';
import PurchaseRecommendations from '../pages/PurchaseRecommendations';
import PurchaseOrders from '../pages/PurchaseOrders';
import PurchaseOrderDetail from '../pages/PurchaseOrderDetail';
import ItemSetup from '../pages/ItemSetup';
import SupplierSetup from '../pages/SupplierSetup';
import SupplierCatalog from '../pages/SupplierCatalog';
import LocationSetup from '../pages/LocationSetup';
import DataHealth from '../pages/DataHealth';
import CostImport from '../pages/CostImport';
import Exports from '../pages/Exports';
import UserProfile from '../pages/UserProfile';
import StoreSettings from '../pages/StoreSettings';
import PlansPage from '../pages/PlansPage';
// Staff-only:
import CustomerOrders from '../pages/CustomerOrders';
import ForecastGenerator from '../pages/ForecastGenerator';
import SafetyStockGenerator from '../pages/SafetyStockGenerator';
import SearchAnalytics from '../pages/SearchAnalytics';
import StoreActivity from '../pages/StoreActivity';
import StoreDataHealth from '../pages/StoreDataHealth';
import StaffGate from '../components/common/StaffGate';

const gated = (element) => <StaffGate>{element}</StaffGate>;

// Each entry: { path, element, titleKey, staffOnly }
// - path: absolute path (with leading slash); supports react-router params
// - element: the rendered React node for that path
// - titleKey: i18n key for the page's one name — its tab title, and the name
//   the command palette and the reports hub list it under. It is the sidebar
//   label; a bare verb there ("Create") gains its section ("Create purchase
//   orders"). Sidebar.test pins it; routeRegistry.test requires one per route.
// - staffOnly: true → route is wrapped in StaffGuard in appRoutes
//
// Order doesn't matter for correctness; kept grouped by feature area to
// ease scanning when adding a new page.
export const REGISTERED_ROUTES = [
  // Workspace landing
  { path: '/', element: <Home />, titleKey: 'nav.home', staffOnly: false },

  // Forecasting
  { path: '/forecast', element: <Forecast />, titleKey: 'nav.forecast', staffOnly: false },
  { path: '/promotions', element: <Promotions />, titleKey: 'nav.promotions', staffOnly: false },

  // Inventory
  { path: '/inventory', element: <Inventory />, titleKey: 'nav.inventory', staffOnly: false },

  // Reports
  { path: '/reports', element: <ReportsHub />, titleKey: 'nav.reports', staffOnly: false },
  { path: '/reports/sales-forecast-accuracy', element: <SalesForecastAccuracyReport />, titleKey: 'nav.reportSalesForecastAccuracy', staffOnly: false },
  { path: '/reports/stockout', element: <StockoutReport />, titleKey: 'nav.reportStockout', staffOnly: false },
  { path: '/reports/excess-stock', element: <ExcessStockReport />, titleKey: 'nav.reportExcess', staffOnly: false },
  { path: '/reports/classification', element: <ClassificationReport />, titleKey: 'nav.reportClassification', staffOnly: false },
  { path: '/reports/gmroi', element: <GMROIReport />, titleKey: 'nav.reportGmroi', staffOnly: false },
  { path: '/reports/dead-stock', element: <DeadStockReport />, titleKey: 'nav.reportDeadStock', staffOnly: false },

  // Purchasing
  { path: '/purchase-recommendations', element: <PurchaseRecommendations />, titleKey: 'nav.createPurchaseOrders', staffOnly: false },
  // Same page, opened on its "Due today" view (the stockout alert mail's
  // link). A SEPARATE path rather than a query param: the tab router matches
  // on pathname only. Titled "Create purchase orders — due today" once mounted.
  { path: '/purchase-recommendations/due-today', element: <PurchaseRecommendations dueToday />, titleKey: 'nav.createPurchaseOrders', staffOnly: false },
  // Record route — the tab reads the page's loading name until the page sets
  // it to the PO number.
  // The optional `receive` segment opens the same page with the Receive
  // goods shortcut applied (Home and list "Receive" buttons);
  // hooks/useOpenPurchaseOrder keeps one tab per PO across both spellings.
  { path: '/purchase-orders/:id/:mode?', element: <PurchaseOrderDetail />, titleKey: 'purchaseOrderDetail.loadingTitle', staffOnly: false },
  { path: '/purchase-orders', element: <PurchaseOrders />, titleKey: 'nav.managePurchaseOrders', staffOnly: false },

  // Setup
  { path: '/item-setup', element: <ItemSetup />, titleKey: 'nav.items', staffOnly: false },
  // Same page, opened filtered to one item by its code (Data Health's "Edit
  // item"). A SEPARATE entry rather than an optional segment on the line
  // above: only a trailing segment can be optional, so `/item-setup/item/
  // :itemCode?` would stop matching the bare `/item-setup`. The page titles the
  // tab "Items — <item>" once mounted, so the two tabs are distinguishable in
  // the strip.
  { path: '/item-setup/item/:itemCode', element: <ItemSetup />, titleKey: 'nav.items', staffOnly: false },
  { path: '/supplier-setup', element: <SupplierSetup />, titleKey: 'nav.suppliers', staffOnly: false },
  // Same page, opened with the search box seeded to one supplier (Data
  // Health's "Edit supplier"); titled "Suppliers — <supplier>" once mounted.
  { path: '/supplier-setup/supplier/:supplier', element: <SupplierSetup />, titleKey: 'nav.suppliers', staffOnly: false },
  // Record-link route — the page sets its own "Supplier catalog — <supplier>" title once
  // the supplier loads; the generic nav.supplierCatalog is the pre-load fallback.
  // The optional :itemCode segment lets a deep-link (Data Health "Fix order
  // rules") land the grid filtered to one item — the tab router matches on
  // pathname only, so a query param can't carry it.
  { path: '/suppliers/:id/catalog/:itemCode?', element: <SupplierCatalog />, titleKey: 'nav.supplierCatalog', staffOnly: false },
  { path: '/locations-setup', element: <LocationSetup />, titleKey: 'nav.locations', staffOnly: false },

  // Other (not in sidebar) — reached via the user dropdown / the phone's More
  // sheet / command palette. Each owns a dedicated nav.* title key (same convention
  // as the sidebar routes) so the tab title is route-owned, not borrowed
  // from a menu/page-heading namespace, and not the raw path.
  { path: '/data-health', element: <DataHealth />, titleKey: 'nav.dataHealth', staffOnly: false },
  { path: '/cost-import', element: <CostImport />, titleKey: 'nav.costImport', staffOnly: false },
  // The async export rail's recovery path — the only way back to a generated
  // file once the tab that asked for it has gone (see pages/Exports.jsx).
  { path: '/exports', element: <Exports />, titleKey: 'nav.exports', staffOnly: false },
  { path: '/user-profile', element: <UserProfile />, titleKey: 'nav.userProfile', staffOnly: false },
  { path: '/store-settings', element: <StoreSettings />, titleKey: 'nav.storeSettings', staffOnly: false },
  // The billing home — also where Shopify returns a merchant after a plan
  // switch (backend billing/constants.py RETURN_SCREENS pins this path).
  { path: '/plans', element: <PlansPage />, titleKey: 'nav.plans', staffOnly: false },

  // Staff-only — element pre-wrapped with StaffGate so the guard
  // applies on both URL navigation and tab re-render (the latter
  // bypasses react-router's outlet chain).
  { path: '/customer-orders', element: gated(<CustomerOrders />), titleKey: 'nav.customerOrders', staffOnly: true },
  { path: '/forecast-generator', element: gated(<ForecastGenerator />), titleKey: 'nav.forecastGenerator', staffOnly: true },
  { path: '/safety-stock-generator', element: gated(<SafetyStockGenerator />), titleKey: 'nav.safetyStockGenerator', staffOnly: true },
  { path: '/search-analytics', element: gated(<SearchAnalytics />), titleKey: 'nav.searchAnalytics', staffOnly: true },
  { path: '/store-activity', element: gated(<StoreActivity />), titleKey: 'nav.storeActivity', staffOnly: true },
  // Same page opened on one store — the operator digest mail links here
  // (backend api/services/operator_digest.py STORE_ACTIVITY_PATH).
  { path: '/store-activity/store/:store', element: gated(<StoreActivity />), titleKey: 'nav.storeActivity', staffOnly: true },
  { path: '/store-data-health', element: gated(<StoreDataHealth />), titleKey: 'nav.storeDataHealth', staffOnly: true },
];

export function findRouteForPath(path) {
  if (!path) return null;
  for (const route of REGISTERED_ROUTES) {
    if (matchPath({ path: route.path, end: true }, path)) return route;
  }
  return null;
}

// Returns the route params extracted from `tabPath` by matching it
// against the registered routes. For example, tabPath
// '/purchase-orders/123' matched against the registered
// '/purchase-orders/:id' returns `{ id: '123' }`. Returns
// `{}` for tab paths that have no params or don't match any route.
//
// Consumed by TabsHost to populate TabParamsContext per tab, replacing
// react-router's useParams() (which is global to the URL, not per-tab).
export function paramsForTabPath(tabPath) {
  if (!tabPath) return {};
  for (const route of REGISTERED_ROUTES) {
    const m = matchPath({ path: route.path, end: true }, tabPath);
    if (m) return m.params || {};
  }
  return {};
}

// Returns the React element for a tab's path, or null if the path is not
// a registered route. TabsHost falls back to a "not found" placeholder
// in that case rather than crashing — a malformed sessionStorage entry
// shouldn't break the whole shell.
export function findElementForPath(path) {
  return findRouteForPath(path)?.element ?? null;
}

export function getTitleKeyForPath(path) {
  const route = findRouteForPath(path);
  if (route) return route.titleKey;
  // A path no route matches (a malformed sessionStorage tab, which TabsHost
  // shows as "not found") is named by the path itself: the strip's
  // `t(value, { defaultValue: value })` renders it verbatim.
  return path;
}
